import { Card, Col, Descriptions, Flex, Row, Tag } from 'antd';
import { Space } from '@monorepo/react-components';
import React, { FunctionComponent, ReactNode } from 'react';
import {
  AssetOrigin,
  Languages,
  Series as SeriesType,
  SeriesSubject,
  SocialAccount,
  TimezoneLabels,
  Voiceover,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import SocialIcon from '../../components/social-provider-icon/social-provider-icon';
import { compact, startCase } from 'lodash';
import { useTranslation } from 'react-i18next';

import './series-information.scss';

const getSeriesTypeColor = (type: AssetOrigin) => {
  switch (type) {
    case AssetOrigin.AIImages:
      return 'green';
    case AssetOrigin.StockImages:
      return 'orange';
    default:
      return 'default';
  }
};

interface Props {
  columns?: number;
  series: SeriesType;
  actions?: ReactNode;
}

export const SeriesInformation: FunctionComponent<Props> = ({
  series,
  actions,
  columns = 2,
}) => {
  const { t } = useTranslation();
  const {
    dataStore: { voiceoverStore, socialAccountStore },
  } = useStore();

  const socialAccountIds =
    series.socialAccountIds && series.socialAccountIds.length > 0
      ? series.socialAccountIds
      : compact([series.socialAccountId]);

  const voiceOver = voiceoverStore.get(series.voiceoverId);
  const socialAccounts = socialAccountIds.map((id) =>
    socialAccountStore.get(id)
  );

  const seriesItems = [
    {
      label: t('series.information.category'),
      children: startCase(
        Object.keys(SeriesSubject).find(
          (key) => SeriesSubject[key] === series.subjectType
        )
      ),
    },
    {
      label: t('series.information.language'),
      children: Object.keys(Languages).find(
        (key) => Languages[key] === series.language
      ),
    },
    {
      label: t('series.information.voice'),
      children: startCase(voiceOver?.name),
    },
    {
      label: t('series.information.videosLength'),
      children: `${series.duration} seconds`,
    },
    {
      label: t('series.information.visualStyle'),
      children: (
        <Tag color={getSeriesTypeColor(series.assetOrigin)}>
          <div>
            {startCase(
              Object.keys(AssetOrigin).find(
                (key) => AssetOrigin[key] === series.assetOrigin
              )
            )}{' '}
          </div>
          <div>{series.assetStyle}</div>
        </Tag>
      ),
    },
    {
      label: t('series.information.socialAccounts'),
      children:
        socialAccounts?.length > 0 ? (
          <Flex style={{ flexDirection: 'column', minHeight: '50px' }} gap={4}>
            {socialAccounts.map((account, index) => {
              if (!account) {
                return null;
              }

              return (
                <Flex key={index} align={'center'} gap={8} justify={'center'}>
                  <SocialIcon provider={account?.provider} />
                  <span>{account.title}</span>
                </Flex>
              );
            })}
          </Flex>
        ) : (
          t('series.information.noSocialAccounts')
        ),
    },
    {
      label: t('series.information.status'),
      children: (
        <Tag color={series.isActive ? 'green' : 'red'}>
          {series.isActive
            ? t('series.card.active')
            : t('series.card.disabled')}
        </Tag>
      ),
      span: 1,
    },
    {
      label: t('series.information.postingFrequency'),
      children: `${series.videosPerDay || 1}`,
      span: 1,
    },
    {
      label: t('series.information.timeZone'),
      children: `${TimezoneLabels[series.timezone]}`,
      span: 1,
    },
  ];

  return (
    <Card
      className={'series-information'}
      title={
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
                {series.name}
              </span>
            </Space>
          </Col>
          <Col>
            <Space>{actions ? actions : null}</Space>
          </Col>
        </Row>
      }
    >
      <Descriptions column={columns} items={seriesItems} />
    </Card>
  );
};
